import { useState, useEffect, useCallback, useMemo } from 'react';
import { goalService, Goal, SubGoal, Todo } from '../services/goalService';
import { calculateOverallProgress, countCompletedGoals } from '../utils/goalProgress';
import { touched } from '../utils/goalOrder';
import { useTranslation } from '../i18n';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../types/error';
import { logger } from '../utils/logger';
import { trackEvent } from '../utils/analytics';

interface UseGoalsReturn {
  goals: Goal[];
  loading: boolean;
  isInitialLoad: boolean;
  error: string | null;
  loadGoals: () => Promise<void>;
  addGoal: (goalData: Partial<Goal>) => Promise<Goal>;
  updateGoal: (id: string, data: Partial<Goal>) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  toggleFavorite: (goalId: string) => Promise<void>;
  addSubGoal: (goalId: string, title: string, description: string) => Promise<void>;
  updateSubGoal: (goalId: string, subGoalId: string, data: Partial<SubGoal>) => Promise<void>;
  deleteSubGoal: (goalId: string, subGoalId: string) => Promise<void>;
  updateTodo: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void>;
  importGoals: (importedGoals: Goal[]) => void;
  overallProgress: number;
  completedGoalsCount: number;
}

export const useGoals = (isAuthenticated: boolean): UseGoalsReturn => {
  const { t } = useTranslation();
  const toast = useToast();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mutation failures surface as toasts; the page-level error is reserved for load failures.
  const failMutation = useCallback((err: unknown, fallbackKey: string, retry?: () => Promise<unknown>): never => {
    logger.error(fallbackKey, err);
    const message = getErrorMessage(err, t(fallbackKey));
    toast.error(message, {
      actions: retry
        ? [{ label: t('common.retryAction'), onClick: () => { retry().catch(() => undefined); } }]
        : undefined,
    });
    throw new Error(message);
  }, [t, toast]);

  const loadGoals = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await goalService.getGoals();
      setGoals(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.unknown');
      setError(t('errors.loadGoals', { message: errorMessage }));
      logger.error('Error loading goals', err);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [isAuthenticated, t]);

  const addGoal = useCallback(async (goalData: Partial<Goal>): Promise<Goal> => {
    try {
      const newGoal = await goalService.createGoal(goalData);
      trackEvent('goal_created', { method: 'manual' });
      setGoals(prev => [...prev, newGoal]);
      return newGoal;
    } catch (err) {
      return failMutation(err, 'errors.addGoal');
    }
  }, [failMutation]);

  const updateGoal = useCallback(async (id: string, data: Partial<Goal>): Promise<void> => {
    try {
      const updatedGoal = await goalService.updateGoal(id, data);
      setGoals(prev => prev.map(goal => goal.id === id ? updatedGoal : goal));
    } catch (err) {
      return failMutation(err, 'errors.updateGoal', () => updateGoal(id, data));
    }
  }, [failMutation]);

  const deleteGoal = useCallback(async (id: string): Promise<void> => {
    try {
      await goalService.deleteGoal(id);
      setGoals(prev => prev.filter(goal => goal.id !== id));
    } catch (err) {
      return failMutation(err, 'errors.deleteGoal', () => deleteGoal(id));
    }
  }, [failMutation]);

  const toggleFavorite = useCallback(async (goalId: string): Promise<void> => {
    if (!goalId) return;

    try {
      const response = await goalService.toggleFavorite(goalId);
      setGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, isFavorite: response.isFavorite } : goal
      ));
    } catch (err) {
      return failMutation(err, 'errors.toggleFavorite', () => toggleFavorite(goalId));
    }
  }, [failMutation]);

  const addSubGoal = useCallback(async (goalId: string, title: string, description: string) => {
    try {
      const newSubGoal = await goalService.createSubGoal(goalId, { title, description });
      setGoals(prev => prev.map(goal => 
        goal.id === goalId 
          ? touched({ ...goal, subGoals: [...goal.subGoals, newSubGoal] })
          : goal
      ));
    } catch (err) {
      return failMutation(err, 'errors.addSubGoal');
    }
  }, [failMutation]);

  const updateSubGoal = useCallback(async (goalId: string, subGoalId: string, data: Partial<SubGoal>): Promise<void> => {
    try {
      const updatedSubGoal = await goalService.updateSubGoal(goalId, subGoalId, data);
      setGoals(prev => prev.map(goal => 
        goal.id === goalId 
          ? touched({
              ...goal, 
              subGoals: goal.subGoals.map(sg => 
                sg.id === subGoalId ? updatedSubGoal : sg
              )
            })
          : goal
      ));
    } catch (err) {
      return failMutation(err, 'errors.updateSubGoal', () => updateSubGoal(goalId, subGoalId, data));
    }
  }, [failMutation]);

  const deleteSubGoal = useCallback(async (goalId: string, subGoalId: string): Promise<void> => {
    try {
      await goalService.deleteSubGoal(goalId, subGoalId);
      setGoals(prev => prev.map(goal => 
        goal.id === goalId 
          ? touched({ ...goal, subGoals: goal.subGoals.filter(sg => sg.id !== subGoalId) })
          : goal
      ));
    } catch (err) {
      return failMutation(err, 'errors.deleteSubGoal', () => deleteSubGoal(goalId, subGoalId));
    }
  }, [failMutation]);

  const updateTodo = useCallback(async (subGoalId: string, todoId: string, data: Partial<Todo>): Promise<void> => {
    try {
      const updatedTodo = await goalService.updateTodo(subGoalId, todoId, data);
      setGoals(prev => prev.map(goal => !goal.subGoals.some(sg => sg.id === subGoalId) ? goal : touched({
        ...goal,
        subGoals: goal.subGoals.map(subGoal => 
          subGoal.id === subGoalId 
            ? {
                ...subGoal,
                todos: subGoal.todos.map(todo => 
                  todo.id === todoId ? updatedTodo : todo
                )
              }
            : subGoal
        )
      })));
    } catch (err) {
      return failMutation(err, 'errors.updateTodo', () => updateTodo(subGoalId, todoId, data));
    }
  }, [failMutation]);

  const importGoals = useCallback((importedGoals: Goal[]) => {
    setGoals(importedGoals);
    setError(null);
  }, []);

  const overallProgress = useMemo(() => calculateOverallProgress(goals), [goals]);
  const completedGoalsCount = useMemo(() => countCompletedGoals(goals), [goals]);

  useEffect(() => {
    if (isAuthenticated) {
      loadGoals();
    }
  }, [isAuthenticated, loadGoals]);

  return {
    goals,
    loading,
    isInitialLoad,
    error,
    loadGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    toggleFavorite,
    addSubGoal,
    updateSubGoal,
    deleteSubGoal,
    updateTodo,
    importGoals,
    overallProgress,
    completedGoalsCount
  };
};
